"use client";

import { useState } from "react";
import { usePathname } from "next/navigation";
import Link from "next/link";
import { Wordmark } from "@/components/wordmark";
import { MenuIcon } from "@/components/icons";

const PORTAL_LOGIN_URL = "https://portal.kleipdata.co.uk/login";

const NAV_LINKS = [
  { label: "Platform", href: "/#platform" },
  { label: "Features", href: "/features" },
  { label: "How It Works", href: "/#workflow" },
  { label: "Why KleipData", href: "/#benefits" },
  { label: "Pricing", href: "/pricing" },
  { label: "API", href: "/docs" },
];

export function SiteHeader() {
  const [open, setOpen] = useState(false);
  const pathname = usePathname();
  const onRoute = NAV_LINKS.some((link) => link.href === pathname);
  const isActive = (href: string, index: number) =>
    href === pathname || (!onRoute && index === 0);

  return (
    <header className="sticky top-0 z-50 border-b border-white/10 bg-brand-navy/95 text-white backdrop-blur-md">
      <div className="mx-auto flex h-20 max-w-[1440px] items-center justify-between px-container-padding">
        <Link href="/" aria-label="KleipData home" className="text-white">
          <Wordmark />
        </Link>

        <nav className="hidden items-center gap-gutter md:flex">
          {NAV_LINKS.map((link, i) => (
            <Link
              key={link.label}
              href={link.href}
              aria-current={isActive(link.href, i) ? "page" : undefined}
              className={`text-label-md transition-colors hover:text-brand-cyan ${
                isActive(link.href, i) ? "text-white" : "text-white/80"
              }`}
            >
              {link.label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-stack-md">
          <a
            href={PORTAL_LOGIN_URL}
            className="hidden text-label-md text-white/80 transition-colors hover:text-white md:block"
          >
            Login
          </a>
          <Link
            href="/request-demo"
            className="hidden rounded-md border border-brand-cyan px-4 py-2 text-label-md text-brand-cyan transition-colors hover:bg-brand-cyan/10 lg:block"
          >
            Request Demo
          </Link>
          <Link
            href="/get-access"
            className="rounded-md bg-gradient-to-r from-brand-cyan to-brand-electric px-5 py-2.5 text-label-md text-white transition-all hover:shadow-[0_0_15px_rgba(0,217,217,0.5)]"
          >
            Get Access
          </Link>
          <button
            type="button"
            aria-label="Toggle menu"
            aria-expanded={open}
            onClick={() => setOpen((v) => !v)}
            className="text-white transition-colors hover:text-brand-cyan md:hidden"
          >
            <MenuIcon className="h-7 w-7" />
          </button>
        </div>
      </div>

      {open && (
        <nav className="border-t border-white/10 px-container-padding pb-4 pt-2 md:hidden">
          <ul className="flex flex-col divide-y divide-white/10">
            {NAV_LINKS.map((link, i) => (
              <li key={link.label}>
                <Link
                  href={link.href}
                  onClick={() => setOpen(false)}
                  aria-current={isActive(link.href, i) ? "page" : undefined}
                  className={`block py-3 text-label-md hover:text-brand-cyan ${
                    isActive(link.href, i) ? "text-white" : "text-white/80"
                  }`}
                >
                  {link.label}
                </Link>
              </li>
            ))}
            <li>
              <Link
                href="/request-demo"
                onClick={() => setOpen(false)}
                className="block py-3 text-label-md text-white/80 hover:text-brand-cyan"
              >
                Request Demo
              </Link>
            </li>
            <li>
              <Link
                href="/get-access"
                onClick={() => setOpen(false)}
                className="block py-3 text-label-md text-white/80 hover:text-brand-cyan"
              >
                Get Access
              </Link>
            </li>
            <li>
              <a
                href={PORTAL_LOGIN_URL}
                className="block py-3 text-label-md text-white/80 hover:text-brand-cyan"
              >
                Login
              </a>
            </li>
          </ul>
        </nav>
      )}
    </header>
  );
}
